/**
 * Banner shown at the top of /ops/dashboard for users who logged in via OTP
 * and never picked a password. Dismissed for the current session via sessionStorage.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldAlert, X } from 'lucide-react';
import { authApi } from '@/services/api';

const DISMISSED_KEY = 'trackotic_set_password_banner_dismissed';

export default function SetPasswordBanner() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(DISMISSED_KEY) === '1') return;
    let alive = true;
    authApi.me()
      .then((r) => { if (alive && r.data?.user && r.data.user.has_password === false) setShow(true); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!show) return null;

  const dismiss = () => {
    sessionStorage.setItem(DISMISSED_KEY, '1');
    setShow(false);
  };

  return (
    <div
      className="mb-4 rounded-xl border border-amber-300/30 bg-amber-500/10 backdrop-blur p-3 flex items-center gap-3"
      data-testid="set-password-banner"
    >
      <ShieldAlert className="w-5 h-5 text-amber-300 shrink-0" />
      <div className="flex-1 text-sm">
        <p className="text-amber-100 font-medium">You're logged in via OTP.</p>
        <p className="text-amber-200/80 text-xs">
          Set a password to log in faster next time —{' '}
          <Link to="/account/set-password" className="underline font-semibold" data-testid="set-password-link">
            do it now
          </Link>
          .
        </p>
      </div>
      <button
        onClick={dismiss}
        aria-label="Dismiss"
        className="text-amber-200/70 hover:text-amber-100 p-1"
        data-testid="dismiss-set-password-banner"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
